import React, { FC, useCallback, useMemo, useState } from 'react';

import { BUTTON_TYPES, EvPopup, TITLE_MODES } from '@evinced-private/ui-common';

import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';

import { AddLabelTestsModal } from '../add-label-tests-modal';
import { AddLabelDataProps } from '../add-label-tests-modal/AddLabelTestsModal.types';
import { addLabels, removeLabels, updateLabels } from '../api/LabelUpdateApiService';
import { TestRowSharedType } from '../helpers/types';
import { LABELS } from '../labels';
import { RemoveLabelTestsModal } from '../remove-label-tests-modal';
import { UpdateTestsModal } from '../update-tests-modal';

import { LABEL_MANAGEMENT_MODAL_CONFIG, LABEL_MANAGEMENT_MODAL_TYPE } from './constants';
import {
	LabelManagementSharedModalProps,
	LabelManagementSharedModalState,
	RemoveLabelDataProps,
	UpdateLabelDataProps
} from './LabelManagementSharedModal.types';
import { baseGetModalTitle } from './utils';

export const LabelManagementSharedModal: FC<LabelManagementSharedModalProps> = (props) => {
	const [onDataChangeProps, setOnDataChangeProps] = useState<LabelManagementSharedModalState>(null);

	const {
		type,
		isOpen,
		onClose,
		onSave,
		testRows,
		tenantConfiguredLabels,
		configuredValues,
		appType
	} = props;

	const config = LABEL_MANAGEMENT_MODAL_CONFIG[type];

	const handleCloseAction = useCallback((): void => {
		onClose();
	}, [onClose]);

	const onDataChange = useCallback(
		(data: AddLabelDataProps | RemoveLabelDataProps | UpdateLabelDataProps): void => {
			setOnDataChangeProps(data);
		},
		[]
	);

	const handleSaveAction = useCallback(async () => {
		if (type === LABEL_MANAGEMENT_MODAL_TYPE.ADD) {
			await addLabels(
				testRows as TestRowSharedType[],
				(onDataChangeProps as AddLabelDataProps).additions,
				tenantConfiguredLabels,
				appType
			);
		}
		if (type === LABEL_MANAGEMENT_MODAL_TYPE.REMOVE) {
			const data = onDataChangeProps as RemoveLabelDataProps;
			const removalStrings = data.removals.map((r) => r.label.value as string);
			await removeLabels(testRows as TestRowSharedType[], removalStrings, appType);
		}
		if (type === LABEL_MANAGEMENT_MODAL_TYPE.UPDATE) {
			const data = onDataChangeProps as UpdateLabelDataProps;
			await updateLabels(
				testRows as TestRowSharedType[],
				data.changes,
				tenantConfiguredLabels,
				appType
			);
		}

		onSave();
	}, [type, onSave, testRows, onDataChangeProps, tenantConfiguredLabels, appType]);

	const isSaveButtonDisabled = useMemo(() => {
		if (!onDataChangeProps) {
			return true;
		}

		if (type === LABEL_MANAGEMENT_MODAL_TYPE.ADD) {
			const data = onDataChangeProps as AddLabelDataProps;
			return !data.additions || data.additions.length === 0;
		}

		if (type === LABEL_MANAGEMENT_MODAL_TYPE.UPDATE) {
			const data = onDataChangeProps as UpdateLabelDataProps;
			return !data.changes || data.changes.length === 0;
		}

		if (type === LABEL_MANAGEMENT_MODAL_TYPE.REMOVE) {
			const data = onDataChangeProps as RemoveLabelDataProps;
			return !data.removals || data.removals.length === 0;
		}

		return true;
	}, [type, onDataChangeProps]);

	const buttons = useMemo(
		() => [
			{
				title: LABELS.CANCEL_BUTTON_TEXT,
				onClick: handleCloseAction,
				type: BUTTON_TYPES.GHOST
			},
			{
				title: LABELS.SAVE_BUTTON_TEXT,
				onClick: handleSaveAction,
				disabled: isSaveButtonDisabled
			}
		],
		[handleCloseAction, handleSaveAction, isSaveButtonDisabled]
	);
	const MODAL_CONTENT = useMemo(
		() => ({
			[LABEL_MANAGEMENT_MODAL_TYPE.ADD]: (
				<AddLabelTestsModal
					testRows={testRows as TestRowSharedType[]}
					tenantConfiguredLabels={tenantConfiguredLabels}
					configuredValues={configuredValues}
					onDataChange={onDataChange}
				/>
			),
			[LABEL_MANAGEMENT_MODAL_TYPE.UPDATE]: (
				<UpdateTestsModal
					testRows={testRows as TestRowSharedType[]}
					tenantConfiguredLabels={tenantConfiguredLabels}
					configuredValues={configuredValues}
					onDataChange={onDataChange}
					appType={appType}
				/>
			),
			[LABEL_MANAGEMENT_MODAL_TYPE.REMOVE]: (
				<RemoveLabelTestsModal
					testRows={testRows as TestRowSharedType[]}
					tenantConfiguredLabels={tenantConfiguredLabels}
					configuredValues={configuredValues}
					onDataChange={onDataChange}
					appType={appType}
				/>
			)
		}),
		[testRows, tenantConfiguredLabels, configuredValues, onDataChange, appType]
	);
	const title = useMemo(() => {
		return baseGetModalTitle(type, testRows as TestRowSharedType[]);
	}, [type, testRows]);

	return (
		<EvPopup
			appElement={PLATFORM_APP_ID}
			isOpen={isOpen}
			className={config.className}
			onClose={handleCloseAction}
			popupWidth="600px"
			title={title}
			titleMode={TITLE_MODES.MAIN}
			buttons={buttons}
			isCentered={true}
			isControlled
			isBgTransparent={true}
		>
			{MODAL_CONTENT[type]}
		</EvPopup>
	);
};
